import { useMemo, useState } from "react";

import { CreateLeadFormOutput } from "components/leads/create_lead/utils";
import { useLeadsSavedToEntities } from "lib/hooks/api/leads/useLeadsSavedToEntities";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { EventNames, useTracking } from "lib/tracking";
import { pluralise } from "lib/utils";
import { BulkSaveEntity, bulkSaveFormEntities, bulkSaveLookupIds } from "./bulkSaveEntityConfig";

export type UseBulkSaveLeadResult = {
  isDrawerOpen: boolean;
  setIsDrawerOpen: (open: boolean) => void;
  isInitialLoading: boolean;
  savedLeadIds: string[];
  defaultValues: Partial<CreateLeadFormOutput>;
  tooltipContent: string;
  handleClick: () => void;
};

/**
 * Drives the bulk "Save to lead" button over a (possibly mixed-source) selection: resolves which
 * leads the entities are already saved to, builds the create-lead form defaults, owns the drawer
 * open state, and logs the click. All per-entity-type behaviour (e.g. Cognism contacts, whose ids
 * are redeemIds) lives in {@link BULK_SAVE_ENTITY_CONFIG}, so this hook stays source-agnostic.
 */
export function useBulkSaveLead(entities: BulkSaveEntity[]): UseBulkSaveLeadResult {
  const { logEvent } = useTracking();
  const { data: currentUser } = useCurrentUser();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { data: savedLeads, isInitialLoading } = useLeadsSavedToEntities(
    bulkSaveLookupIds(entities),
  );
  const savedLeadIds = useMemo(() => savedLeads?.map((lead) => lead.id) ?? [], [savedLeads]);

  const defaultValues: Partial<CreateLeadFormOutput> = useMemo(
    () => ({ assignedToId: currentUser?.guid, entities: bulkSaveFormEntities(entities) }),
    [currentUser?.guid, entities],
  );

  // Both bulk-savable types are labelled "contact", so a mixed selection reads uniformly.
  const tooltipContent = `Save ${pluralise(entities.length, "contact")} to a lead`;

  const handleClick = () => {
    logEvent(EventNames.saveDataClicked, {
      "Context source": "Bulk action",
      "Data type": "CONTACT",
      "Entity count": entities.length,
    });
    setIsDrawerOpen(true);
  };

  return {
    isDrawerOpen,
    setIsDrawerOpen,
    isInitialLoading,
    savedLeadIds,
    defaultValues,
    tooltipContent,
    handleClick,
  };
}
